import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { sampleReceiptsApi, type Sample } from '../../api/endpoints.js';
import { useLaboratoryTimeZone } from '../../app/laboratory-time-zone.js';
import { ActionButton, Panel } from '../../components/ui/index.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { ReceiptDocumentActions } from './ReceiptDocumentActions.js';
import { recordLinkClass } from './RecordHeader.js';

/**
 * The receipt a sample arrived in, read once per page: this card reads the
 * work order the receipt names from it. Since D325 the record bar reads the
 * client and the project from the sample's own work order instead.
 */
export function useSampleReceiptDetail(sample: Sample) {
  const receiptId = sample.receipt?.id ?? null;
  return useQuery({
    queryKey: ['sample-receipt', receiptId],
    queryFn: () => sampleReceiptsApi.get(receiptId as string),
    enabled: receiptId !== null,
  });
}

/**
 * Receipt Information on a sample's page (D213): the handover the sample
 * arrived in, and the way back to it — View Receipt, Print Receipt and
 * Download PDF.
 *
 * These are the facts of the handover AS ISSUED (D325). A sample received
 * without a work order and linked later keeps "none on the receipt" here, with
 * when and by whom it was linked; its work order, client and project now are
 * the record bar's, never copied into the receipt. A sample received before
 * receipts existed (0013) has none, and the card says so rather than showing
 * an empty receipt.
 */
export function SampleReceiptCard({ sample }: { readonly sample: Sample }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = currentLocale();
  const timeZone = useLaboratoryTimeZone();
  const receipt = useSampleReceiptDetail(sample);

  const moment = (iso: string) =>
    formatDateTime(iso, { locale, ...(timeZone ? { timezone: timeZone } : {}) });
  const detail = receipt.data;
  // A sample linked after receipt: the receipt named none, and still does.
  const linkedLater = Boolean(sample.workOrderLink);
  const workOrder = linkedLater
    ? null
    : (detail?.workOrder ??
      (sample.workOrderId && sample.jobNumber
        ? { id: sample.workOrderId, workOrderNumber: sample.jobNumber }
        : null));

  return (
    <Panel title={t('receipts.infoTitle')} description={t('receipts.infoHint')}>
      {/*
        The chain of records, in the order they came to be: the receipt the
        sample arrived in, then the work order it belongs to.
      */}
      <dl className="flex flex-col gap-2.5">
        <ChainRow label={t('receiving.receiptNo')}>
          {sample.receipt ? (
            <Link
              to={`/samples/receipts/${sample.receipt.id}`}
              className={`measure ${recordLinkClass}`}
              dir="ltr"
            >
              {sample.receipt.receiptNumber}
            </Link>
          ) : (
            <span className="text-default-600">{t('receiving.noReceipt')}</span>
          )}
        </ChainRow>
        <ChainRow label={t('samples.jobNumber')}>
          {workOrder ? (
            <Link
              to={`/work-orders/${workOrder.id}`}
              className={`measure ${recordLinkClass}`}
              dir="ltr"
            >
              {workOrder.workOrderNumber}
            </Link>
          ) : (
            <span className="text-default-600" data-testid="receipt-work-order-none">
              {t(linkedLater ? 'receipts.noWorkOrderOnReceipt' : 'samples.noWorkOrder')}
            </span>
          )}
        </ChainRow>
        {sample.workOrderLink ? (
          // D324: the receipt named no work order; the sample was linked later.
          <p className="text-caption text-default-600" data-testid="sample-work-order-linked">
            {t('receipts.linkedAfter', {
              date: moment(sample.workOrderLink.linkedAt),
              name: sample.workOrderLink.linkedBy.displayName ?? t('common.notRecorded'),
            })}
          </p>
        ) : null}
        <ChainRow label={t('receipts.arrival')}>{moment(sample.receivedAt)}</ChainRow>
        <ChainRow label={t('receipts.deliveredBy')}>{sample.receivedFrom}</ChainRow>
        <ChainRow label={t('receiving.receivedBy')}>
          {sample.receivedBy?.displayName ?? t('common.notRecorded')}
        </ChainRow>
      </dl>
      {sample.receipt ? (
        <div className="mt-stack flex flex-col items-start gap-inline border-t border-default-200 pt-stack">
          <ActionButton
            priority="secondary"
            size="sm"
            onPress={() => navigate(`/samples/receipts/${sample.receipt?.id ?? ''}`)}
          >
            {t('receipts.view')}
          </ActionButton>
          <ReceiptDocumentActions
            receiptId={sample.receipt.id}
            receiptNumber={sample.receipt.receiptNumber}
            size="sm"
          />
        </div>
      ) : null}
    </Panel>
  );
}

/** One link of the chain: a caption above its value, sized for a side column. */
function ChainRow({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-caption text-default-600">{label}</dt>
      <dd className="mt-0.5 break-words text-body text-default-900">{children}</dd>
    </div>
  );
}
